/* eslint-disable no-param-reassign */
/* eslint-disable no-restricted-globals */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosInstance, AxiosPromise, AxiosRequestConfig } from 'axios';

/**
 * Контекст ошибки, который передаётся в `clientOptions.onError`.
 * Сделан плоским и сериализуемым — host-приложение само решит,
 * что отправлять в Sentry/Datadog/etc.
 *
 * Важно: тело запроса (`params`) сюда НЕ кладётся — там может быть PII
 * (email, телефон, имя клиента). Если конкретному endpoint'у нужны
 * sanitized-параметры — пусть host-апп прокидывает их через свой
 * api-wrapper.
 */
export type ApiErrorContext = {
    method: string;
    url: string;
    status?: number;
    /** Сетевой код axios: ECONNABORTED/ERR_CANCELED/... — для 0-status кейсов. */
    code?: string;
    /**
     * Сообщение axios-ошибки (`error.message`), например «timeout of 0ms
     * exceeded». Нужно классификатору, чтобы отличить фантомный обрыв
     * запроса от реального сконфигурированного таймаута.
     */
    message?: string;
    /** имя ApiMethodDeclaration, например `users.me` */
    apiKey: string;
    /**
     * Бизнес-код ошибки от бэка (`errorCode` из ErrorData). Лежит в теле
     * 4xx-ответа. Используем для классификации (см. `classifyApiError`):
     * generic коды → debug-уровень, конкретные (`SLOT_TAKEN`, `PROMO_INVALID`)
     * → business-уровень.
     */
    errorCode?: string;
    /** Изначальный бизнес-код, до маппинга бэком на generic (`originalErrorCode`). */
    originalErrorCode?: string;
    /** Человекочитаемое сообщение от бэка (`ErrorData.message`). */
    backendMessage?: string;
    /**
     * Сериализуемое тело ответа сервера (только структурированные поля
     * `ErrorData`, без всего payload'а — чтобы случайно не утащить PII).
     */
    responseData?: {
        errorId?: string;
        application?: string;
        errorCode?: string;
        originalErrorCode?: string;
        message?: string;
    };
};

/**
 * 4xx считаем «бизнес-ответом» (валидация, конфликты, 401), их host-апп
 * через onError тоже получит и сам решит, репортить ли. По умолчанию шлём
 * хосту ВСЁ кроме самых шумных — пусть сам отфильтрует.
 */
function buildErrorContext(
    error: any,
    axiosConfig: AxiosRequestConfig,
    apiKey: string,
): ApiErrorContext {
    // Тело ответа от бэка приходит как `response.data: ErrorData`. Для blob-
    // экспортов (XLSX/PDF) бэк может вернуть Blob даже на 4xx — тогда у нас
    // тут будет инстанс Blob, который сериализовать в Sentry бессмысленно.
    // Host-апп уже распаковывает JSON-Blob в `useApiMethod` через
    // `error.response.data.text() → JSON.parse`, но onError вызывается
    // раньше этого хэндлинга. Поэтому здесь просто читаем `data` как есть
    // и пробрасываем только если это похоже на объект.
    const rawData = error?.response?.data;
    const responseData =
        rawData && typeof rawData === 'object' && !(rawData instanceof Blob)
            ? {
                  errorId: rawData.errorId as string | undefined,
                  application: rawData.application as string | undefined,
                  errorCode: rawData.errorCode as string | undefined,
                  originalErrorCode: rawData.originalErrorCode as string | undefined,
                  message: rawData.message as string | undefined,
              }
            : undefined;

    return {
        apiKey,
        method: (axiosConfig?.method as string | undefined)?.toUpperCase() || 'GET',
        url: axiosConfig?.url ?? apiKey,
        status: error?.response?.status,
        code: error?.code,
        message: error?.message,
        errorCode: responseData?.errorCode,
        originalErrorCode: responseData?.originalErrorCode,
        backendMessage: responseData?.message,
        responseData,
    };
}

const getAuthLoginPathBySubdomain = () => {
    const subdomain = window.location.hostname.split('.')[0]?.toLowerCase();

    if (subdomain === 'admin') return '/auth/admins';

    return '/auth/admins';
};

export type InitApiClientOptions = {
    /** Кастомный клиент (например React Native с interceptors и без редиректа в браузере). */
    axiosInstance?: AxiosInstance;
    /** Вызывается при 401 вместо очистки cookie и редиректа на веб-авторизацию. */
    onUnauthorized?: () => void;
    /**
     * Опциональный единый «sink» для всех ошибок API: 4xx и 5xx, network-fail.
     * Используется host-приложением, чтобы прокинуть ошибки в Sentry/Datadog.
     * Чтобы не залить аналитику 401/422 — фильтр на стороне host'а
     * (см. `appyApiOptions` в каждом фронте).
     */
    onError?: (error: unknown, ctx: ApiErrorContext) => void;
    /**
     * Слать ли credentials (cookies) с каждым запросом. Default — `true`
     * для обратной совместимости (admin/auth/app полагаются на
     * session-cookie). Виджет, наоборот, явно передаёт `false`: cookies
     * на gateway-домене больше не нужны (auth — через
     * `Authorization: Bearer`), и их отправка из embed-виджета на
     * чужом домене провоцировала cross-profile collision (см.
     * `WidgetGuard` / `SlotDetailsService`).
     */
    withCredentials?: boolean;
};

export type ErrorData = {
    errorId: string;
    application: string;
    errorCode: string;
    message: string;
    originalErrorCode?: string;
};

export function initApi<T>(
    apiDeclaration: ApiDeclaration<T>,
    baseURL: string,
    clientOptions?: InitApiClientOptions,
) {
    const api = {} as Api<T>;
    const http: AxiosInstance | typeof axios = clientOptions?.axiosInstance ?? axios;

    // Wrap with axios and apply config extenders
    Object.keys(apiDeclaration).forEach((key) => {
        api[key] = async (params = undefined) => {
            const etag = params?.etag;

            if (etag) {
                delete params.etag;
            }

            let axiosConfig = apiDeclaration[key](params);

            const headers = axiosConfig.headers as Record<string, unknown> | undefined;
            const mergedHeaders = {
                ...(headers || {}),
                ...(etag ? { 'If-None-Match': etag } : {}),
            };
            const hasConditionalHeaders = Boolean(mergedHeaders['If-None-Match']);

            axiosConfig = {
                ...axiosConfig,
                baseURL,
                withCredentials: clientOptions?.withCredentials ?? true,
                headers: mergedHeaders,
                ...(hasConditionalHeaders
                    ? {
                          validateStatus: (status) =>
                              (status >= 200 && status < 300) || status === 304,
                      }
                    : {}),
            };

            try {
                const result = await http(axiosConfig);

                return result;
            } catch (error) {
                const e: any = error;

                if (e?.response?.status === 401) {
                    if (clientOptions?.onUnauthorized) {
                        clientOptions.onUnauthorized();
                    } else if (typeof document !== 'undefined' && typeof location !== 'undefined') {
                        document.cookie = 'user=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
                        document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
                        if (!location.href.includes('auth')) {
                            const redirectUrl = encodeURIComponent(window.location.href);

                            location.href = `${process.env.REACT_APP_AUTH_DOMAIN}${getAuthLoginPathBySubdomain()}?redirect=${redirectUrl}`;
                        }
                    }
                }

                if (clientOptions?.onError) {
                    try {
                        clientOptions.onError(e, buildErrorContext(e, axiosConfig, key));
                    } catch {
                        // онError — observer-уровень, не должен ломать основной flow
                    }
                }

                throw error;
            }
        };

        api[key].displayName = key;
    });

    return api;
}

export type ApiMethodDeclaration<ParamsData, _ResponseData, DisplayName = ''> = {
    (params: ParamsData): AxiosRequestConfig;
    displayName?: DisplayName;
};

export type ApiDeclaration<T> = {
    [P in keyof T]: T[P];
};

export type Api<T> = {
    [P in keyof T]: MakeServiceFromServiceDeclaration<T[P], P>;
};

export type MakeServiceFromServiceDeclaration<T, DisplayName> = {
    (params: ExtractApiMethodParamsType<T>): AxiosPromise<ExtractApiMethodResponseDataType<T>>;
    displayName: DisplayName;
};

type ExtractApiMethodResponseDataType<T> =
    T extends ApiMethodDeclaration<infer _P, infer D> ? D : any;

type ExtractApiMethodParamsType<T> = T extends ApiMethodDeclaration<infer P, infer _D> ? P : any;

export type ExtendServiceParams<T> = ApiMethodDeclaration<
    ExtractApiMethodParamsType<T>,
    ExtractApiMethodResponseDataType<T>
>;
